import * as React from "react";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Urls } from "../../../../../Urls";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { OrderSetupReview } from "../../../Common/Review/Component/OrderSetupReview";
import { ITableConfig, Table } from "../../../../../../Shared/Table/Table";
import { Order } from "../../../../../../../Wrap/Models/Domains/Orders/Order";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { equlityBondWarning, hasDecisionMaker } from "../../Resource/Content";
import { DocumentsHelper } from "../../../PortfolioDashboard/Components/Document/Resource/DocumentsHelper";
import { DocumentTaskRequestTypeEnum } from "../../../../../../../Wrap/Constants/Enums/Tasks/DocumentTaskRequestTypeEnum";
import { DownloadButton } from "../../../../../../Shared/DownloadButton/DownloadButton";
import { CommissionChargeCancelMsg } from "../../../Common/AdviserCharges/Components/CommissionChargeCancelMsg";
import { ExternalFundDocument } from "../../../Common/Documents/Components/ExternalFundDocument";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { CashProductDeclarations } from "../../../Common/Review/Component/CashProductDeclarations";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import { Instrument } from "../../../../../../../Wrap/Models/Domains/Investment/Instrument";
import { InstrumentSearchQuery } from "../../../../../../../Wrap/Models/Domains/Investment/InstrumentSearchQuery";
import { InvestmentSelectionAction } from "../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { SwitchableHolding } from "../../Store/SwitchStoreStatic";
import { BuyAction } from "../../../Buy/Action/BuyAction";
import { ClientTradableInvestments } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/ClientTradableInvestments";

interface IReviewProps extends IProcessComponentProps {
    type: SubAccountType;
    onClearError: VoidFunction;
}

interface IReviewState {
    isCashDeclarationChecked: boolean | null;
    isDocLoading: boolean;
    isLoadingData: boolean;
    holdings: SwitchableHolding[];
    instrumentDetails: Record<number, Instrument>;
}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isCashDeclarationChecked: false,
            isDocLoading: false,
            isLoadingData: true,
            holdings: [],
            instrumentDetails: {},
        }
    }

    componentDidMount() {
        super.componentDidMount();
        this.loadHoldingsAndInstruments();
    }

    private loadHoldingsAndInstruments() {
        const params = getQueryParameters();
        const sellAllocations = this.props.data?.Contribution?.SwitchInstructionModel?.SellAllocations || [];
        const buyAllocations = this.props.data?.Contribution?.SwitchInstructionModel?.BuyAllocations || [];
        const allProductIds = [
            ...sellAllocations.map(alloc => alloc.ProductId),
            ...buyAllocations.map(alloc => alloc.ProductId),
        ].filter((id, index, self) => self.indexOf(id) === index);

        if (allProductIds.length === 0) {
            this.setState({ isLoadingData: false });
            return;
        }

        this.setState({ isLoadingData: true });

        BuyAction.getReuseHoldings(
            params["hierarchyid"],
            params["subaccounthierarchyid"],
            0,
            (data: ClientTradableInvestments) => {
                const individual = data.CurrentIndividualHoldings || [];
                const model = data.CurrentModelHoldings || [];
                const ids = [...individual.map(ele => ele.ProductId), ...model.map(ele => ele.ProductId)];

                const query: InstrumentSearchQuery = {
                    ProductWrapperTypes: [this.props.type],
                    ProductIds: allProductIds.length > 0 ? allProductIds : ids,
                    CanBeSold: true,
                    ResultsPerPage: Math.max(allProductIds.length, ids.length),
                    ServiceProposition: data.AccountServiceProposition,
                };

                InvestmentSelectionAction.searchInstruments(
                    query,
                    params["subaccounthierarchyid"],
                    (result: Instrument[]) => {
                        const holdings: SwitchableHolding[] = [];
                        const instrumentDetails = { ...this.state.instrumentDetails };
                        result.forEach((instrument) => {
                            const individualHolding = individual.find(h => h.ProductId === instrument.ProductId);
                            const modelHolding = model.find(h => h.ProductId === instrument.ProductId);
                            const holding = individualHolding || modelHolding;

                            instrumentDetails[instrument.ProductId] = instrument;

                            if (holding) {
                                holdings.push({
                                    ProductId: holding.ProductId,
                                    ProductCode: holding.InstrumentCode,
                                    Name: holding.DisplayName,
                                    ProductType: holding.Type,
                                    MarketValue: holding.MarketValue || { Amount: 0, Currency: CurrencyCode.GBP },
                                    CurrQuantity: holding.CurrQuantity || 0,
                                    OriginQuantity: holding.OriginQuantity || holding.CurrQuantity || 0,
                                    SellOrders: holding.SellOrders || 0,
                                    UnitPrice: holding.UnitPrice || { Amount: 0, Currency: CurrencyCode.GBP },
                                    IsDeferred: instrument?.IsDeferred || false,
                                    HasOpenValueSellOrder: holding.HasOpenValueSellOrder || false,
                                    FactsheetUrl: holding.FinexFactSheetUrl,
                                });
                            }
                        });

                        this.setState({
                            holdings,
                            instrumentDetails,
                            isLoadingData: false,
                        });
                    }
                );
            }
        );
    }

    private getTotalSwitchValue(): number {
        const sellAllocations = this.props.data?.Contribution?.SwitchInstructionModel?.SellAllocations || [];
        return sellAllocations.reduce((total, allocation) => total + (allocation.Value?.Amount || 0), 0);
    }

    private getSellTableColumn(): ITableConfig[] {
        return [
            {
                title: "Investment",
                key: "Name",
                render: (data, row) => {
                    const factsheetUrl = row.FactsheetUrl;
                    const name = row.Name;
                    return (
                        <>
                            {factsheetUrl ? (
                                <a href={factsheetUrl} target="_blank" rel="noopener noreferrer">
                                    {name}
                                </a>
                            ) : (
                                <p className="mb-0">{name}</p>
                            )}
                            {name.toLowerCase() !== "gbp cash" && (
                                <p className="mb-0">{row.ProductCode || ""}</p>
                            )}
                        </>
                    );
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "Current units held",
                key: "Quantity",
                render: (data, row) => (row.Quantity !== undefined ? row.Quantity.toFixed(4) : "N/A"),
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Current estimated value",
                key: "MarketValue",
                render: (data, row) => MoneyDisplayHelper.getMoneyFormat(row.MarketValue || { Amount: 0, Currency: CurrencyCode.GBP }),
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Estimated value to sell",
                key: "ValueAmount",
                render: (data, row) => MoneyDisplayHelper.getMoneyFormat({ Amount: Number(row.ValueAmount.toFixed(2)), Currency: CurrencyCode.GBP }),
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
        ];
    }

    handleCashDeclarationChange = (event) => {
        this.setState({ isCashDeclarationChecked: event });
      };

    private getBuyTableColumn(): ITableConfig[] {
        return [
            {
                title: "Investment",
                key: "Name",
                render: (data, row) => {
                    const factsheetUrl = row.FactsheetUrl;
                    const name = row.Name;
                    return (
                        <>
                            {factsheetUrl ? (
                                <a href={factsheetUrl} target="_blank" rel="noopener noreferrer">
                                    {name}
                                </a>
                            ) : (
                                <p className="mb-0">{name}</p>
                            )}
                            {name.toLowerCase() !== "gbp cash" && (
                                <p className="mb-0">{row.ProductCode || ""}</p>
                            )}
                        </>
                    );
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "% Allocation",
                key: "Percentage",
                render: (data, row) => `${Number(row.Percentage.toFixed(2))}%`,
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
        ];
    }

    private onNavi = (path) => {
        this.props.onClearError();
        Navigator.navigateToAndPreserveQueryParameters(path);
    };

    private renderDocuments() {
        const claccountid = getQueryParameters()["claccountid"];
        const headAccountHierarchyId = getQueryParameters()["hierarchyid"];
        if(this.props.data?.Contribution?.SwitchInstructionModel?.InstructionId) {
            const chargesInformationQuery = DocumentsHelper.getTaskRequestForChargeInformation(
            this.props.data.Contribution.SwitchInstructionModel.InstructionId,
            claccountid,
            DocumentTaskRequestTypeEnum.ChargesInformation,
            true
        );

        const config = [
            {
                title: (
                    <div className="mb-24">
                        <a className="hyperlink">Charges Information</a>
                        <DownloadButton
                            request={chargesInformationQuery}
                            selectedAccount={headAccountHierarchyId}
                        ></DownloadButton>
                    </div>
                ),
                data: " ",
            },
        ];
        return (
            <>
                <Typography type="wider" wrapCss="narrow-margin no-print" config={config}></Typography>
            </>
        );
        }
    }

    private renderPensionIllustration(instructionId: number, subClAccountId: string, clientAccountHierarchyId: string) {
        const illustrationQuery = DocumentsHelper.getTaskRequestForPreSalePensionIllustration(
            instructionId,
            subClAccountId,
            DocumentTaskRequestTypeEnum.PreSalePensionIllustration
        );

        return (
            <div className="mb-24">
                <a className="hyperlink">Pre-Sale Pension Illustration</a>
                <DownloadButton
                    request={illustrationQuery}
                    selectedAccount={clientAccountHierarchyId}
                ></DownloadButton>
            </div>
        )
    }

    public render(): JSX.Element {
        const { data, type } = this.props;
        const sellAllocations = data?.Contribution?.SwitchInstructionModel?.SellAllocations || [];
        const buyAllocations = data?.Contribution?.SwitchInstructionModel?.BuyAllocations || [];
        const totalSwitchValue = this.getTotalSwitchValue();

        const sellData = sellAllocations.filter(ele => ele.Value?.Amount > 0).map(alloc => {
            const holding = this.state.holdings.find(h => h.ProductId === alloc.ProductId);
            const instrument = this.state.instrumentDetails[alloc.ProductId];
            return {
                ProductId: alloc.ProductId,
                Name: instrument?.Name || alloc.Name || "Unknown",
                Type: alloc.Type,
                Value: alloc.Value,
                ValueAmount: alloc.Value?.Amount || 0,
                Percentage: alloc.Percentage || 0,
                Quantity: holding?.CurrQuantity || 0,
                ProductCode: instrument?.ProductCode || "",
                MarketValue: holding?.MarketValue || { Amount: 0, Currency: CurrencyCode.GBP },
                FactsheetUrl: instrument?.FinexDocumentUrl || holding?.FactsheetUrl || "",
            };
        });

        const buyData = buyAllocations.filter(ele => ele.Percentage > 0).map(alloc => {
            const holding = this.state.holdings.find(h => h.ProductId === alloc.ProductId);
            const instrument = this.state.instrumentDetails[alloc.ProductId];
            return {
                ProductId: alloc.ProductId,
                Name: instrument?.Name || alloc.Name || "Unknown",
                Type: alloc.Type,
                Value: alloc.Value,
                ValueAmount: alloc.Value?.Amount || 0,
                Percentage: alloc.Percentage,
                ProductCode: instrument?.ProductCode || "",
                FactsheetUrl: instrument?.FinexDocumentUrl || holding?.FactsheetUrl || "",
            };
        });

        const instrumentCodes = buyData.map(item => item.ProductCode || "");

        const needAlert = hasDecisionMaker(data.SellInvestmentList) || hasDecisionMaker(data.BuyInvestmentList);
        return (
            <>
                {needAlert && <Alert alertType={AlertType.Warning} message={equlityBondWarning} />}
                <h2 className="mb-30">
                    <span>Review</span>
                </h2>
                <CommissionChargeCancelMsg processType={ProcessType.Switch} />
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Order setup</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => {
                            this.props.onClearError();
                            Navigator.navigateTo(Urls.get().SwitchOrderSetup, {
                                scrollAfterNavigating: true,
                                queryParamsObject: {
                                    type: getQueryParameters()["type"],
                                    claccountid: getQueryParameters()["claccountid"],
                                    hierarchyid: getQueryParameters()["hierarchyid"],
                                    subaccounthierarchyid: getQueryParameters()["subaccounthierarchyid"],
                                    batchId: this.props.data.BatchId,
                                },
                            });
                        }}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <OrderSetupReview process={ProcessType.Sell} subAccountType={type} data={data} colNum={2} />

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Switch from investments</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => this.onNavi(Urls.get().SwitchFrom)}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <Table
                    data={sellData}
                    col={this.getSellTableColumn()}
                    TotalBody={[
                        { data: "Estimated total to switch", colSpan: 3 },
                        {
                            data: MoneyDisplayHelper.FromatMoneyDisplay(totalSwitchValue, CurrencyCode.GBP, true, 2),
                            tfTdCssWrapper: "cell-right",
                            colSpan: 1,
                        },
                    ]}
                />

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Switch to investments</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => {
                            this.onNavi(Urls.get().SwitchTo);
                        }}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <Table
                    data={buyData}
                    col={this.getBuyTableColumn()}
                    TotalBody={[
                        { data: "Total", colSpan: 1 },
                        {
                            data: `${Number(buyData.reduce((total, ele) => total + ele.Percentage, 0).toFixed(2))}%`,
                            tfTdCssWrapper: "cell-right",
                            colSpan: 1,
                        },
                    ]}
                />

                <hr className="mt-0" />
                <h3 className="mb-8 h4">Explaining charges to your client</h3>
                <p className="mb-10">
                    It's important that your client understands the costs and charges they will incur based on their
                    investment choices. The Charges Information Document provides the aggregated costs and charges for
                    your client's investment.
                </p>
                <br />
                {this.renderDocuments()}
                {(this.props.type === SubAccountType.Npr || this.props.type === SubAccountType.JuniorSipp) &&
                this.renderPensionIllustration(
                    HierarchyIdHelper.generateHierarchyId(this.props.data?.Contribution?.HierarchyId).entityId,
                    getQueryParameters()["claccountid"],
                    getQueryParameters()["hierarchyid"]
                    )}
                {!this.state.isLoadingData && (
                    <ExternalFundDocument instrumentCodes={instrumentCodes} {...this.props} />
                )}
                <div className="no-print">
                    <CheckboxEditor
                        value={data.AdviceGiven}
                        onChange={(v) => {
                            this.props.onChange({ AdviceGiven: v });
                        }}
                        label="Please indicate if advice was given on this trade."
                        cssWrapper="square-checkbox block no-print"
                    ></CheckboxEditor>
                </div>
                {buyData.some(x => x.Type === ProductType.CashProduct) && (
                    <CashProductDeclarations
                        instrumentCodes={buyData.filter(x => x.Type === ProductType.CashProduct).map(x => x.ProductCode || "")}
                        isCashDeclarationChecked={this.state.isCashDeclarationChecked}
                        onCashDeclarationChanged={this.handleCashDeclarationChange}
                    />
                )}
            </>
        );
    }
}
